import { connect } from "react-redux";

import type { Bus } from "@busbud/int-schemas/udm";
import type { Vehicle } from "@busbud/int-schemas/udm/schemas";

import type { LegCardIdClassAndFare } from "@app/constants/seat-selection-first";
import { isOutboundLeg } from "@app/helpers/udm/get-searched-leg";
import { parseLegCardIdClassAndFareHash } from "@app/helpers/udm/hashes";
import { isBoXOnewayTrip } from "@app/helpers/udm/trip";
import { getSearch } from "@app/modules/results/selectors";
import { getLegCardOptionsMatrix } from "@app/modules/results/selectors/leg-cards/get-options";
import { getTripOptionPerSegmentId } from "@app/modules/results/selectors/leg-cards/get-options-per-segment-id";
import { getLegSourceNames } from "@app/modules/results/selectors/legs/get-leg-source-names";
import {
  getTrips,
  getTripsSegments
} from "@app/modules/results/selectors/state-accessors";
import { getSegmentsWithSeatSelectionByTripId } from "@app/modules/results/selectors/trips/get-segments-with-seat-selection-by-trip-id";
import { getSegmentsWithUnknownVehicleLayout } from "@app/modules/results/selectors/trips/get-segments-with-unknown-vehicle-layout";
import { getTripSegmentLocationName } from "@app/modules/results/selectors/trips/get-trip-segment-location-name";
import { getTripSegments } from "@app/modules/results/selectors/trips/get-trip-segments";
import { TripDetailsFetchStatusKey } from "@app/modules/results/state/trip-details-fetch-status";
import { reconciliateTripId } from "@app/modules/seat-selection-first/helpers/renconciliate-trip-id";
import type { ResultsReduxState } from "@app/types/results-redux-types";
import type { TripOption, TripOptionsMatrix } from "@app/types/results-types";
import type { SearchInfo } from "@app/types/search-types";

import type { ConnectedProps, MapStateToProps } from "react-redux";

import { SeatSelection as SeatSelectionComponent } from "./seat-selection";

interface StateProps {
  arrival_place_name: string;
  departure_place_name: string;
  direction: string;
  is_external: boolean;
  is_vehicle_not_found: boolean;
  next_segment_id?: string; // In case of multiple segments to have seat selection
  search: SearchInfo;
  segment_id: string;
  source_names: string[];
  trip_id: string;
  trip_option: TripOption | null;
  trip_options_matrix: TripOptionsMatrix;
  trip_segment_ids: string[];
  vehicle_layouts: Record<string, Vehicle<"v3">["layout"]>;
  vehicle?: Bus;
}

export interface OwnProps {
  onClose?: () => void;
  outbound_leg_card_id_class_and_fare: LegCardIdClassAndFare;
  return_leg_card_id_class_and_fare?: LegCardIdClassAndFare | null;
  segment_id: string;
}

const EMPTY_ARR: unknown[] = [];
const EMPTY_OBJ: Record<string, unknown> = {};

export const mapStateToProps: MapStateToProps<
  StateProps,
  OwnProps,
  ResultsReduxState
> = (state: ResultsReduxState, own_props: OwnProps): StateProps => {
  const {
    segment_id,
    outbound_leg_card_id_class_and_fare,
    return_leg_card_id_class_and_fare
  } = own_props;

  const trips = getTrips(state);
  const segments = getTripsSegments(state);
  const search = getSearch(state);
  const is_inventory_ready = state.inventory_ready;

  const leg_card_id_class_and_fare =
    return_leg_card_id_class_and_fare || outbound_leg_card_id_class_and_fare;

  try {
    const { leg_card_id } = parseLegCardIdClassAndFareHash(
      leg_card_id_class_and_fare
    );

    const trip = reconciliateTripId(
      Object.values(trips),
      segments,
      leg_card_id_class_and_fare,
      search,
      state.config.experiments
    );

    const trip_id = trip.id;

    const segments_with_seat_selection = getSegmentsWithSeatSelectionByTripId(
      state,
      trip_id
    );

    const segments_with_unknown_seat_selection =
      getSegmentsWithUnknownVehicleLayout(state, trip_id);

    const segment_with_seat_selection = segments_with_seat_selection.find(
      segment => segment.id === segment_id
    )!;

    const vehicle = segment_with_seat_selection?.vehicle as Bus;
    const trip_option = getTripOptionPerSegmentId(state, trip_id, segment_id);
    const curr_segment_idx = segments_with_seat_selection.findIndex(
      segment => segment.id === segment_id
    );

    const next_segment_id: string | undefined =
      segments_with_seat_selection.length > 1
        ? segments_with_seat_selection[curr_segment_idx + 1]?.id
        : undefined;

    const source_names = getLegSourceNames(state, trip_id);
    const is_external = isBoXOnewayTrip(search, trip);

    // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    const trip_options_matrix = getLegCardOptionsMatrix(state, leg_card_id);

    const is_vehicle_not_found = Boolean(
      (state.inventory_ready &&
        segments_with_seat_selection.length === 0 &&
        segments_with_unknown_seat_selection.length === 0) ||
        (state.trip_details_fetch_status[trip_id] &&
          state.trip_details_fetch_status[trip_id] ===
            TripDetailsFetchStatusKey.Error)
    );

    const { origin_name, destination_name } = getTripSegmentLocationName(
      state,
      segment_id
    );

    const departure_place_name = origin_name;
    const arrival_place_name = destination_name;
    const direction = isOutboundLeg(
      trip.trip_legs.find(leg => leg.segment_ids.includes(segment_id))?.leg || 1
    )
      ? "outbound"
      : "return";

    const vehicle_layouts = getTripSegments(state, trip_id).reduce<
      Record<string, Vehicle<"v3">["layout"]>
    >(
      (acc, segment) => {
        // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        acc[segment.id] = segment.vehicle.layout;
        return acc;
      },
      {} as Record<string, Vehicle<"v3">["layout"]>
    );

    const trip_segment_ids = trip.segment_ids;
    return {
      arrival_place_name,
      departure_place_name,
      direction,
      is_external,
      is_vehicle_not_found,
      next_segment_id,
      search,
      segment_id,
      source_names,
      trip_id,
      trip_option,
      trip_options_matrix,
      trip_segment_ids,
      vehicle_layouts,
      vehicle
    };
  } catch (e) {
    return {
      arrival_place_name: "",
      departure_place_name: "",
      direction: "",
      is_external: false,
      is_vehicle_not_found: is_inventory_ready,
      search,
      segment_id: "",
      source_names: [],
      trip_id: "",
      trip_option: null,
      trip_options_matrix: EMPTY_ARR as TripOptionsMatrix,
      trip_segment_ids: EMPTY_ARR as string[],
      vehicle_layouts: EMPTY_OBJ as Record<string, Vehicle<"v3">["layout"]>
    };
  }
};

const connector = connect(mapStateToProps);

export type ConnectorProps = ConnectedProps<typeof connector> & OwnProps;

export const SeatSelection = connector(SeatSelectionComponent);
